import React from 'react';

import { SubscriptionCreate } from 'App/components/Subscriptions/SubscriptionsForm';
import RequestMail from 'App/components/Subscriptions/components/RequestMailDialog';

import {
  Action,
  Actions,
  ActionMessage,
  ActionBar,
  ViewColumnIcon,
  DownloadIcon,
  WarningAmberIcon,
} from '@gogame/components';
import { FullScreenToggler } from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenDispatch,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import RegsStatsColumnsPicker from 'scenes/RegsStats/RegsStatsColumnsPicker';
import { REGS_STATS_PAGE } from 'scenes/RegsStats/constants';
import { useExportCSV, useSendToEmail } from 'scenes/RegsStats/data';
import { useFiltersWithPeriodType } from 'scenes/RegsStats/hooks';
import { APPLY, REORDER_COLUMNS } from 'scenes/RegsStats/reducers';

const Toolbar = ({ columnsIgnoredInExport }) => {
  const [title] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();

  const [exportCSV] = useExportCSV();
  const [sendToEmail] = useSendToEmail();

  const {
    columns: { order: columnsOrder },
  } = screenState.temporary || screenState;

  const handleCustomizeColumns = () => {
    openChildComponent(RegsStatsColumnsPicker, {
      value: columnsOrder,
      onSubmit: ({ order, apply }) => {
        const action = {
          type: REORDER_COLUMNS,
          payload: order,
        };
        if (apply) {
          dispatch({
            type: APPLY,
            payload: action,
          });
        } else {
          dispatch(action);
        }
      },
    });
  };

  const {
    filters,
    columns: { order },
    tableSettings: { orderBy, orderDir },
    ready,
  } = screenState;

  const handleSendEmail = () => {
    openChildComponent(RequestMail, {
      onSubmit: ({ subject }) => {
        sendToEmail({
          payload: {
            filters,
            tableSettings: { orderBy, orderDir },
            columns: { order },
          },
          name: subject,
        });
      },
      subject: `[GG Regs Stats] Your report is ready`,
    });
  };

  const handleExportCSV = () => {
    exportCSV({
      filters,
      tableSettings: { orderBy, orderDir },
      columns: { order },
    });
  };

  const filtersWithPeriodType = useFiltersWithPeriodType(filters);

  const handleOpenSubscriptionsForm = () => {
    openChildComponent(SubscriptionCreate, {
      page: REGS_STATS_PAGE,
      payload: {
        filters: filtersWithPeriodType,
        tableSettings: { orderBy, orderDir },
        columns: { order },
      },
      withPayTo: false,
    });
  };

  return (
    <React.Fragment>
      <ActionBar
        title={title}
        primaryActions={
          <Actions>
            <Action
              icon={<ViewColumnIcon />}
              label={'Customize columns'}
              onClick={handleCustomizeColumns}
            />
            <Action label={'Export report'} icon={<DownloadIcon />}>
              {columnsIgnoredInExport?.map(({ name }) => (
                <ActionMessage
                  icon={
                    <WarningAmberIcon color={'warning'} fontSize={'small'} />
                  }
                  label={`${name} column will not be available in the export file`}
                />
              ))}
              <Action
                label={'Export as csv'}
                onClick={handleExportCSV}
                disabled={!ready}
              />
              <Action
                label={'Send to email'}
                onClick={handleSendEmail}
                disabled={!ready}
              />
              <Action
                label={'Subscribe'}
                onClick={handleOpenSubscriptionsForm}
                disabled={!ready}
              />
            </Action>
            <Action component={FullScreenToggler} />
          </Actions>
        }
      />
      {childComponent}
    </React.Fragment>
  );
};

export default Toolbar;
